/**
 * /ja/meeting-minutes-ai — 議事録 reframe bridge (JA, single-locale).
 *
 * Targets 議事録フォーマット (13k) / 議事録 ai / 議事録作成 ai / ai議事録無料. Most
 * PM-adjacent of the JA bridges: gives a real 議事録 format, then pivots to
 * 決定記録 (decision log). No EN sibling.
 */
import { MarketingShell } from './MarketingShell';

export function JaMeetingMinutes() {
  return (
    <MarketingShell
      currentLocale="ja"
      hreflangPath="/meeting-minutes-ai"
      hreflangLocales={['ja']}
      documentTitle="議事録フォーマット＋AI作成｜「決定」が残る議事録とは · Draftlize"
      documentDescription="すぐ使える議事録フォーマットとAI自動作成の方法。さらに、議事録の本当の価値である「意思決定」を逃さず残す方法を解説します。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">ガイド · 議事録</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            議事録フォーマットと、<br />
            <em>その後に残すべきもの。</em>
          </h1>
          <p className="lede">
            まずは実用的な議事録フォーマットを置きます。AI で自動作成する方法も紹介します。でも本題はその先です。きれいな議事録ができても、会議の本当の価値——下した<em>意思決定</em>とその理由——はフォルダの中で眠り、数週間で古くなります。議事録は記録であって、生きたプロダクトコンテキストではない。ここでは、議事録から決定を取り出して追跡可能にする方法を扱います。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">すぐ使える議事録フォーマット。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>会議名／日時／参加者</strong>——後で誰に確認すべきかが分かる</li>
            <li><strong>議題（アジェンダ）</strong>——話す予定だったこと</li>
            <li><strong>決定事項</strong>——決めたこと。<em>ここが議事録の心臓部</em></li>
            <li><strong>決定の理由・前提</strong>——なぜそう決めたか（数週間後の自分が一番知りたい部分）</li>
            <li><strong>ToDo（担当者・期限）</strong>——次に誰が動くか</li>
            <li><strong>保留・次回への持ち越し</strong></li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.9, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            録音やオンライン会議があるなら、まず <a href="/ja/transcription" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>文字起こし</a> して、この型に沿って整える——AI 議事録ツールはこの「整える」を自動化します。
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">議事録はフォルダで眠る。<em>決定だけは眠らせない。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            AI で議事録を作るところまでは解決済みです。問題はその後——「決定事項」の数行が、影響する仕様とも、覆した前の決定ともつながらないまま放置されること。Draftlize はその数行を追跡可能なカードにします。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">決定が「カード」になる</h3>
            <p>議事録の「決定事項」を Draftlize に取り込むと、決定・理由・担当者を持つ型付きカードになります。議事録全文を抱える代わりに、会議より長生きすべき数個の決定だけが ID で参照できます。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">決定が前の決定を更新する</h3>
            <p>今日の決定が先月の決定を覆したら、リンクされた古い決定とその上の仕様が自動で stale になります。次の議事録が「もう決めたこと」を蒸し返しません。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">議事録 vs 意思決定ログ</h3>
            <p>議事録は「会議の記録」、意思決定ログは「決定の台帳」。記録は AI に任せ、決定は台帳として最新に保つ——この分業が、決定が静かに古くなる問題を解きます。<a href="/ja/summary" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>要約</a>も同じ考え方です。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          AI はもう議事録を完璧に作る。ただ「その決定が今もまだ有効か」は議事録には書いていない。
        </blockquote>
        <cite>記録はそのまま。決定を台帳として生かすのが Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">よくある質問。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'AI 議事録は無料で使える？', a: '多くの AI 議事録ツールに無料枠があります。Draftlize は議事録の作成ツールではありません——出来た議事録の「決定事項」を、最新に保てるカードに変える役割です。' },
            { q: '録音から議事録を作るには？', a: 'まず文字起こしし、本ページの議事録フォーマットに沿って整理します。AI 議事録ツールはこの整理を自動化します。Draftlize はその後、決定を構造化して追跡します。' },
            { q: '決定事項はどう管理すればいい？', a: '議事録の「決定事項」をカードとして残し、理由と担当者を添えます。後で別の決定がそれを覆したら、依存する箇所が自動で stale 表示になるので、古い決定に気づけます。' },
            { q: '「アクションアイテム」付きの議事録ツールと何が違う？', a: 'アクションアイテムは完了したら消えるタスクです。決定は違います——理由・依存・寿命があり、上流が変われば古くなる。Draftlize はその古さを追跡します。タスク一覧にはできないことです。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">会議の決定を、追跡されるカードに<em>——$5 無料で。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新規登録で $5 付与</span>
          <span>使った分だけ支払い</span>
          <span>残高は無期限</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            議事録ツールはそのまま。決定事項を Draftlize に渡せば、会議の決定が議事録の一行ではなく、最新であり続けるカードになります。
          </p>
          <a className="btn-primary" href="/">$5 無料で始める</a>
        </div>
      </section>
    </MarketingShell>
  );
}
